/**
 * ============================================================================
 * WEEKVIEW.JS v8.0 - CSS CLASS-BASED + ALL v7.4 FEATURES
 * ============================================================================
 *
 * ✅ v8.0: CSS Class-Based Redesign
 *          - Static inline styles → CSS classes (cal-week- namespace)
 *          - Dynamic styles remain inline (positions, grid-column, drag state)
 *          - Dark mode via .calendar-redesign.dark + ThemeContext
 *          - ALL business logic 100% unchanged from v7.4
 *
 * ✅ v7.4: TASK-SYNC - Strikethrough für erledigte Termine
 * ✅ v7.3: DRAG & DROP FIX - formatDateKey nutzt calendarUtils
 * ✅ v7.2: TIMEZONE FIX - lokale Datums-Komponenten
 * ✅ v7.1: Time Slot Click - Klick auf leere Zeitslots
 * ✅ v7.0: Drag & Drop für Termine (HTML5 API)
 * ✅ v6.0: Planivio Rebranding
 * ✅ v5.1: 24-Stunden Ansicht (00:00 - 23:00)
 * ✅ v5.0: Stundenraster, parallele Termine, Zeit-Linie
 * ✅ v4.0: Horizontal Scroll für Mobile, Category Colors
 *
 * @version 8.0.0 - CSS Class-Based
 */

import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { normalizeDateString, formatInputDate } from './calendarUtils';

console.log('📅 WeekView.js v8.0 loaded - CSS CLASS-BASED + PLANIVIO DESIGN ✓');

// ============================================================================
// CONFIGURATION (unchanged)
// ============================================================================
const START_HOUR = 0;
const END_HOUR = 24;
const HOUR_HEIGHT = 40;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const DAY_MIN_WIDTH = 120;

// ============================================================================
// HELPER FUNCTIONS (100% unchanged from v7.4)
// ============================================================================

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const formatDateKey = (date) => {
  if (date instanceof Date) return formatInputDate(date);
  return normalizeDateString(date);
};

const formatTimeCompact = (timeString) => {
  if (!timeString) return '';
  const parts = timeString.split(':');
  return `${parts[0]}:${parts[1]}`;
};

const isToday = (date) => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

const isSunday = (date) => date.getDay() === 0;

const getWeekdayShort = (date) => {
  const weekdays = ['SO', 'MO', 'DI', 'MI', 'DO', 'FR', 'SA'];
  return weekdays[date.getDay()];
};

const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

/**
 * ✅ v8.0: getCategoryStyle ersetzt durch separate Funktionen
 * getCategoryClass → CSS-Klasse für Kategorie
 * getCategoryBorderColor → Border-Farbe (für dynamische inline styles)
 * getCategoryBgColor → Hintergrundfarbe (für dynamische inline styles)
 * getCategoryTextColor → Textfarbe (für dynamische inline styles)
 */
const getCategoryClass = (category) => {
  const map = {
    'arzt': 'medical', 'medical': 'medical',
    'schule': 'school', 'school': 'school',
    'arbeit': 'work', 'work': 'work',
    'privat': 'personal', 'personal': 'personal',
    'sport': 'sport',
    'familie': 'family', 'family': 'family',
    'sonstiges': 'other'
  };
  return map[category?.toLowerCase()] || 'default';
};

const getCategoryBorderColor = (category) => {
  const colors = {
    'arzt': '#EF4444', 'medical': '#EF4444',
    'schule': '#22C55E', 'school': '#22C55E',
    'arbeit': '#8B5CF6', 'work': '#8B5CF6',
    'privat': '#3B82F6', 'personal': '#3B82F6',
    'sport': '#06B6D4',
    'familie': '#EC4899', 'family': '#EC4899',
    'sonstiges': '#4A90A4'
  };
  return colors[category?.toLowerCase()] || '#3B82F6';
};

const getCategoryBgColor = (category, isDark) => {
  const light = {
    'arzt': '#FEE2E2', 'medical': '#FEE2E2',
    'schule': '#DCFCE7', 'school': '#DCFCE7',
    'arbeit': '#EDE9FE', 'work': '#EDE9FE',
    'privat': '#DBEAFE', 'personal': '#DBEAFE',
    'sport': '#CFFAFE',
    'familie': '#FCE7F3', 'family': '#FCE7F3',
    'sonstiges': '#E8F4F8'
  };
  if (isDark) return getCategoryBorderColor(category) + '33';
  return light[category?.toLowerCase()] || '#DBEAFE';
};

const getCategoryTextColor = (category, isDark) => {
  const light = {
    'arzt': '#DC2626', 'medical': '#DC2626',
    'schule': '#16A34A', 'school': '#16A34A',
    'arbeit': '#7C3AED', 'work': '#7C3AED',
    'privat': '#2563EB', 'personal': '#2563EB',
    'sport': '#0891B2',
    'familie': '#DB2777', 'family': '#DB2777',
    'sonstiges': '#4A90A4'
  };
  if (isDark) return getCategoryBorderColor(category);
  return light[category?.toLowerCase()] || '#2563EB';
};

/**
 * Berechnet die Position und Größe eines Termins (unchanged)
 */
const getAppointmentPosition = (appointment) => {
  const timeStr = appointment.time || '08:00';
  const [hours, minutes] = timeStr.split(':').map(Number);
  const startHour = hours + minutes / 60;

  let endHour = startHour + 1;
  const endTimeStr = appointment.endTime || appointment.end_time;
  if (endTimeStr) {
    const [endH, endM] = endTimeStr.split(':').map(Number);
    endHour = endH + endM / 60;
    // Multi-day event: cap at end of current day
    if (appointment.endDate && appointment.endDate !== appointment.date) {
      endHour = END_HOUR;
    }
  }

  const clampedStart = Math.max(startHour, START_HOUR);
  const clampedEnd = Math.min(endHour, END_HOUR);

  const top = (clampedStart - START_HOUR) * HOUR_HEIGHT;
  const height = Math.max((clampedEnd - clampedStart) * HOUR_HEIGHT, 22);

  return { top, height, startHour, endHour };
};

/**
 * Gruppiert überlappende Termine in Spalten (100% unchanged from v7.4)
 */
const calculateColumns = (appointments) => {
  if (appointments.length === 0) return [];

  const sorted = [...appointments].sort((a, b) => {
    const timeA = a.time || '00:00';
    const timeB = b.time || '00:00';
    return timeA.localeCompare(timeB);
  });

  const groups = [];
  sorted.forEach(apt => {
    const pos = getAppointmentPosition(apt);
    let foundGroup = null;
    for (const group of groups) {
      const overlaps = group.some(existing => {
        const existingPos = getAppointmentPosition(existing);
        return !(pos.endHour <= existingPos.startHour || pos.startHour >= existingPos.endHour);
      });
      if (overlaps) { foundGroup = group; break; }
    }
    if (foundGroup) { foundGroup.push(apt); }
    else { groups.push([apt]); }
  });

  groups.forEach(group => {
    const columns = [];
    group.forEach(apt => {
      const pos = getAppointmentPosition(apt);
      let columnIndex = 0;
      let placed = false;
      while (!placed) {
        if (!columns[columnIndex]) { columns[columnIndex] = []; }
        const hasOverlap = columns[columnIndex].some(existing => {
          const existingPos = getAppointmentPosition(existing);
          return !(pos.endHour <= existingPos.startHour || pos.startHour >= existingPos.endHour);
        });
        if (!hasOverlap) {
          columns[columnIndex].push(apt);
          apt._columnIndex = columnIndex;
          placed = true;
        } else { columnIndex++; }
      }
    });
    const totalColumns = columns.length;
    group.forEach(apt => { apt._totalColumns = totalColumns; });
  });

  return sorted;
};

// ============================================================================
// DAY COLUMN COMPONENT
// ============================================================================
const DayColumn = ({ date, appointments, onAppointmentClick, isDark, hours, onAppointmentDrop, draggingId, setDraggingId, dragOverSlot, setDragOverSlot, onTimeSlotClick }) => {
  const dateKey = formatDateKey(date);
  const today = isToday(date);
  const sunday = isSunday(date);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    const tid = setTimeout(() => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    }, 100);
    const onResize = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    window.addEventListener('resize', onResize);
    return () => { clearTimeout(tid); window.removeEventListener('resize', onResize); };
  }, [date]);

  // Drag & Drop Handlers (unchanged from v7.4)
  const handleDragStart = (e, appointment) => {
    if (appointment.isRecurring) { e.preventDefault(); return; }
    e.stopPropagation();
    setDraggingId(appointment.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('appointmentId', appointment.id.toString());
    e.dataTransfer.setData('sourceDate', appointment.date);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    setDraggingId(null);
    setDragOverSlot(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragOver = (e, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot({ date: dateKey, hour });
  };

  const handleDragLeave = () => { setDragOverSlot(null); };

  const handleDrop = (e, hour) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSlot(null);
    const appointmentId = e.dataTransfer.getData('appointmentId');
    if (!appointmentId || !onAppointmentDrop) return;
    const newTime = `${hour.toString().padStart(2, '0')}:00`;
    onAppointmentDrop(appointmentId, dateKey, newTime);
  };

  const handleTimeSlotClick = (hour) => {
    if (!draggingId && onTimeSlotClick) {
      onTimeSlotClick(date, `${String(hour).padStart(2, '0')}:00`);
    }
  };

  // Filter appointments for this day (unchanged)
  const dayAppointments = appointments.filter(apt => formatDateKey(apt.date) === dateKey);
  const allDayApts = dayAppointments.filter(apt => apt.all_day);
  const timedApts = calculateColumns(dayAppointments.filter(apt => !apt.all_day));

  const headerClass = [
    'cal-week-day-col-header',
    today ? 'cal-week-day-col-header--today' : '',
    sunday ? 'cal-week-day-col-header--sunday' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className="cal-week-day-col">

      {/* Day Header */}
      <div className={headerClass}>
        <div className="cal-week-day-col-weekday">
          {getWeekdayShort(date)}
        </div>
        <div className={`cal-week-day-col-num ${today ? 'cal-week-day-col-num--today' : ''} ${sunday ? 'cal-week-day-col-num--sunday' : ''}`}>
          {date.getDate()}
        </div>
      </div>

      {/* Ganztags-Termine */}
      {allDayApts.length > 0 && (
        <div className="cal-week-allday">
          {allDayApts.map(apt => {
            const catClass = getCategoryClass(apt.category);
            const isCompleted = apt.isCompleted || apt.is_completed || false;
            const borderColor = isCompleted ? '#9CA3AF' : getCategoryBorderColor(apt.category);

            return (
              <div
                key={apt.id}
                onClick={() => onAppointmentClick?.(apt)}
                className={`cal-week-allday-event cal-week-allday-event--${catClass} ${isCompleted ? 'cal-week-allday-event--completed' : ''}`}
                style={{ borderLeftColor: borderColor }}
                title={isCompleted ? `✓ ${apt.title} (Erledigt)` : apt.title}
              >
                {isCompleted && '✓ '}{apt.title}
              </div>
            );
          })}
        </div>
      )}

      {/* Zeit-Grid für Termine */}
      <div
        ref={containerRef}
        className="cal-week-time-grid"
        style={{ minHeight: TOTAL_HOURS * HOUR_HEIGHT }}
      >
        {/* Drop Zones */}
        {hours.map(hour => {
          const isOver = dragOverSlot?.date === dateKey && dragOverSlot?.hour === hour;
          return (
            <div
              key={`drop-${hour}`}
              onDragOver={(e) => handleDragOver(e, hour)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, hour)}
              onClick={() => handleTimeSlotClick(hour)}
              className={`cal-week-drop-zone ${isOver ? 'cal-week-drop-zone--active' : ''}`}
              style={{
                top: hour * HOUR_HEIGHT,
                height: HOUR_HEIGHT,
                zIndex: draggingId ? 1000 : 2
              }}
            />
          );
        })}

        {/* Stundenlinien */}
        {hours.map((hour, index) => (
          <React.Fragment key={hour}>
            <div className="cal-week-hour-line" style={{ top: index * HOUR_HEIGHT }} />
            <div className="cal-week-half-hour-line" style={{ top: index * HOUR_HEIGHT + HOUR_HEIGHT / 2 }} />
          </React.Fragment>
        ))}

        {/* Termine */}
        {timedApts.map(apt => {
          const pos = getAppointmentPosition(apt);
          const catClass = getCategoryClass(apt.category);
          const columnCount = apt._totalColumns || 1;
          const columnIndex = apt._columnIndex || 0;

          const isCompleted = apt.isCompleted || apt.is_completed || false;
          const isFromTask = apt.sourceTaskId || apt.source_task_id;

          const columnsPerEvent = Math.floor(100 / columnCount);
          const gridColumnStart = (columnIndex * columnsPerEvent) + 1;
          const gridColumnEnd = gridColumnStart + columnsPerEvent;

          const borderColor = isCompleted ? '#9CA3AF' : getCategoryBorderColor(apt.category);
          const bgColor = isCompleted ? '#E5E7EB' : getCategoryBgColor(apt.category, isDark);
          const textColor = isCompleted ? '#6B7280' : getCategoryTextColor(apt.category, isDark);

          const aptClass = [
            'cal-week-apt',
            `cal-week-apt--${catClass}`,
            isCompleted ? 'cal-week-apt--completed' : '',
            apt.isRecurring ? '' : 'cal-week-apt--draggable'
          ].filter(Boolean).join(' ');

          return (
            <div
              key={apt.id}
              draggable={!apt.isRecurring}
              onDragStart={(e) => handleDragStart(e, apt)}
              onDragEnd={handleDragEnd}
              onClick={() => onAppointmentClick?.(apt)}
              title={`${formatTimeCompact(apt.time)} - ${apt.title}${isCompleted ? ' ✓ Erledigt' : ''}`}
              className={aptClass}
              style={{
                gridColumn: `${gridColumnStart} / ${gridColumnEnd}`,
                top: pos.top + 1,
                height: pos.height - 2,
                background: bgColor,
                borderLeftColor: borderColor,
                zIndex: draggingId === apt.id ? 1 : (3 + columnIndex),
                opacity: draggingId === apt.id ? 0.5 : (isCompleted ? 0.7 : 1),
                pointerEvents: draggingId ? 'none' : 'auto'
              }}
              onMouseEnter={(e) => {
                if (!draggingId) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.zIndex = '50';
                }
              }}
              onMouseLeave={(e) => {
                if (!draggingId) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.zIndex = `${3 + columnIndex}`;
                }
              }}
            >
              <div className="cal-week-apt-time" style={{ color: textColor }}>
                {isCompleted && '✓ '}{formatTimeCompact(apt.time)}{apt.endTime ? ` – ${formatTimeCompact(apt.endTime)}` : ''}
              </div>
              <div
                className="cal-week-apt-title"
                style={{ whiteSpace: pos.height > 35 ? 'normal' : 'nowrap' }}
              >
                {apt.title}
              </div>
              {isFromTask && (
                <div className="cal-week-apt-task-badge">
                  {isCompleted ? '✓' : '📋'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function WeekView({ currentDate, appointments, onAppointmentClick, onAppointmentDrop, onTimeSlotClick }) {
  const { isDark } = useTheme();
  const scrollContainerRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);

  const weekDays = useMemo(() => {
    const monday = getMonday(currentDate);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      return day;
    });
  }, [currentDate]);

  const weekInfo = useMemo(() => {
    const monday = weekDays[0];
    const sunday = weekDays[6];
    return {
      weekNumber: getWeekNumber(monday),
      startDate: `${monday.getDate()}.${monday.getMonth() + 1}.`,
      endDate: `${sunday.getDate()}.${sunday.getMonth() + 1}.${sunday.getFullYear()}`
    };
  }, [weekDays]);

  const hours = [];
  for (let h = START_HOUR; h < END_HOUR; h++) { hours.push(h); }

  const now = new Date();
  const currentHour = now.getHours() + now.getMinutes() / 60;
  const nowLineTop = currentHour >= START_HOUR && currentHour <= END_HOUR
    ? (currentHour - START_HOUR) * HOUR_HEIGHT : null;
  const todayInWeek = weekDays.some(d => isToday(d));

  useEffect(() => {
    if (scrollContainerRef.current && nowLineTop !== null) {
      scrollContainerRef.current.scrollTop = Math.max(0, nowLineTop - 100);
    }
  }, []);

  return (
    <div className="cal-week">
      {/* Header */}
      <div className="cal-week-header">
        <span className="cal-week-header-kw">📅 KW {weekInfo.weekNumber}</span>
        <span className="cal-week-header-range">{weekInfo.startDate} - {weekInfo.endDate}</span>
      </div>

      {/* Scrollable Content */}
      <div ref={scrollContainerRef} className="cal-week-scroll">
        <div className="cal-week-layout">

          {/* Zeit-Spalte */}
          <div className="cal-week-time-col">
            <div className="cal-week-time-col-header">Zeit</div>
            <div className="cal-week-time-col-hours">
              {hours.map(hour => (
                <div key={hour} className="cal-week-time-label" style={{ height: HOUR_HEIGHT }}>
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
              {nowLineTop !== null && todayInWeek && (
                <div className="cal-week-now-line" style={{ top: nowLineTop }}>
                  <div className="cal-week-now-dot" />
                </div>
              )}
            </div>
          </div>

          {/* Tages-Spalten */}
          <div className="cal-week-days-container">
            {nowLineTop !== null && todayInWeek && (
              <div className="cal-week-now-line-full" style={{ top: 54 + nowLineTop }} />
            )}
            {weekDays.map((day, index) => (
              <DayColumn
                key={index}
                date={day}
                appointments={appointments}
                onAppointmentClick={onAppointmentClick}
                onAppointmentDrop={onAppointmentDrop}
                onTimeSlotClick={onTimeSlotClick}
                isDark={isDark}
                hours={hours}
                draggingId={draggingId}
                setDraggingId={setDraggingId}
                dragOverSlot={dragOverSlot}
                setDragOverSlot={setDragOverSlot}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="cal-week-footer">
        <span>👆 Scrollen für mehr Stunden</span>
        <span>|</span>
        <span>← → Wischen für Tage</span>
      </div>
    </div>
  );
}

export default WeekView;